import { ROLES, ROUTES } from '../../utils/constants';

/**
 * Platform administration flags. These are not tenant roles.
 * Missing flags are denied. Superadmin keeps access without storing the flags.
 */
export const PLATFORM_PERMISSIONS = [
  {
    key: 'pricingManagement',
    name: 'Pricing Management',
    description: 'Edit the wholesale catalog and price list. Does not change tenant access or create invoices.',
    route: ROUTES.ADMIN_PRICING,
    navLabel: 'Pricing',
  },
  {
    key: 'billingManagement',
    name: 'Billing Management',
    description: 'View platform vendor billing reports. Does not collect payment or change prices.',
    route: ROUTES.ADMIN_BILLING,
    navLabel: 'Billing',
  },
  {
    key: 'commercialBetaManagement',
    name: 'Commercial Beta',
    description: 'Turn commercial beta estimates on or off for one dealer or tenant. Estimates are not invoices.',
    route: ROUTES.ADMIN_PRICING,
    navLabel: 'Pricing',
  },
  {
    key: 'dealerManagement',
    name: 'Dealer Management',
    description: 'Manage dealers, dealer admins, and dealer tenant assignments.',
    route: ROUTES.ADMIN_DEALERS,
    navLabel: 'Dealers',
  },
  {
    key: 'tenantManagement',
    name: 'Tenant Management',
    description: 'View and update tenants across the platform. Tenant deletion stays limited to Superadmin.',
    route: ROUTES.ADMIN_TENANTS,
    navLabel: 'Tenant Management',
  },
  {
    key: 'usageReporting',
    name: 'Usage Reporting',
    description: 'View usage and margin reports. Reports are not invoices.',
    route: ROUTES.ADMIN_PRICING,
    navLabel: 'Pricing',
  },
  {
    key: 'platformSupport',
    name: 'Platform Support',
    description: 'Work the Signal Bridge support queue.',
    route: ROUTES.ADMIN_SUPPORT,
    navLabel: 'Support',
  },
];

const PRICING_PAGE_KEYS = ['pricingManagement', 'commercialBetaManagement', 'usageReporting'];

export function isSuperadminRole(role) {
  return role === ROLES.SUPERADMIN || role === 'superadmin';
}

export function hasPlatformPermission({ role, permissions } = {}, key) {
  if (isSuperadminRole(role)) return true;
  return permissions?.[key] === true;
}

export function canOpenPricingPage({ role, permissions } = {}) {
  return PRICING_PAGE_KEYS.some((key) => hasPlatformPermission({ role, permissions }, key));
}

export function pricingPageAccess({ role, permissions } = {}) {
  const known = Boolean(role || permissions);
  if (!known) {
    return { catalog: true, wholesale: true, policies: true, usage: true, beta: true };
  }
  const subject = { role, permissions };
  const pricing = hasPlatformPermission(subject, 'pricingManagement');
  return {
    catalog: pricing,
    wholesale: pricing,
    policies: pricing,
    usage: hasPlatformPermission(subject, 'usageReporting'),
    beta: hasPlatformPermission(subject, 'commercialBetaManagement'),
  };
}

/** Links for a person who is not already in the full admin sidebar. */
export function delegatedPlatformNav({ role, permissions, dealerActive = false } = {}) {
  if (dealerActive || isSuperadminRole(role)) return [];
  const items = [];
  const seen = new Set();
  PLATFORM_PERMISSIONS.forEach((item) => {
    if (!hasPlatformPermission({ role, permissions }, item.key)) return;
    if (seen.has(item.route)) return;
    seen.add(item.route);
    items.push({ path: item.route, label: item.navLabel, permission: item.key });
  });
  return items;
}

export function platformPermissionValue(user, key) {
  if (key === 'pricingManagement' && user?.role === 'superadmin') return true;
  const raw = user?.platformPermissions;
  if (raw && typeof raw === 'object' && Object.prototype.hasOwnProperty.call(raw, key)) {
    return raw[key] === true;
  }
  if (key === 'pricingManagement') return user?.capabilities?.pricingManagement === true;
  return false;
}

export function platformPermissionDraft(user) {
  const draft = {};
  PLATFORM_PERMISSIONS.forEach((item) => {
    draft[item.key] = platformPermissionValue(user, item.key);
  });
  return draft;
}
